<template lang="pug">
.form-select(:class='formClasses')
  label.form-select__label(v-if='label', :for='name')
    | {{ label }}
    span.red(v-if='required') *
  input-hidden(:value='value', :name='name')
  element-container.form-select__value(
    :cursor='readonly',
    @click.stop='toggle',
    :class='{ disabled: disabled }'
  )
    .form-select__button(
      v-if='!readonly',
      :class='{ "form-select__button_opened": opened }'
    )
    .form-select__selected(v-if='selected', :title='selected') {{ selected }}
    .form-select__placeholder(v-else) {{ placeholder }}
  element-container.form-select__options(
    v-if='!readonly',
    :class='containerClasses'
  )
    .form-select__filter-container(v-if='filtering', @click.stop='')
      input.form-select__filter-input(
        ref='input',
        v-model='filter',
        @click.stop=''
      )
    .form-select__no-options(v-if='filtering && filtered.length === 0') {{ filterText }}
    .form-select__options-container(:class='scrollClasses', :style='styles')
      .form-select__options-scroll(ref='options')
        .form-select__option(
          v-for='(option, index) in filtered',
          :class='{ "form-select__option_selected": isSelected(option.value) }',
          :key='index',
          @click.stop='input(option)'
        ) {{ option.text }}
        .form-select__add(v-if='addText', @click='$emit("add")') {{ addText }}
  .form-select__text(v-if='vError') {{ errorText }}
</template>

<script>
import ElementContainer from './private/Container.vue';
import InputHidden from './Hidden.vue';

export default {
  components: {
    ElementContainer,
    InputHidden,
  },

  props: {
    autoWidth: {
      type: Boolean,
      default: false,
    },

    error: {
      type: [Boolean, String],
      default: false,
    },

    errorText: {
      type: String,
      default: '',
    },

    filterable: {
      type: [Boolean, String],
      default: false,
      validator: (value) => typeof value === 'boolean' || value === 'auto',
    },

    filterText: {
      type: String,
      default: 'Уточните критерии поиска',
    },

    label: {
      type: String,
      default: '',
    },

    large: {
      type: Boolean,
      default: false,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    name: {
      type: [Boolean, String],
      default: false,
    },

    options: {
      type: Array,
      default: () => [],
    },

    placeholder: {
      type: String,
      default: '',
    },

    addText: {
      type: String,
      default: '',
    },

    size: {
      type: Number,
      default: 8,
    },

    value: {
      type: [Number, String, Array],
      default: null,
    },

    wide: {
      type: Boolean,
      default: false,
    },

    quarter: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    v: {
      type: [Object, Function],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      filter: '',
      height: null,
      opened: false,
      data: [],
    };
  },

  computed: {
    vError() {
      return this.error || (this.v && this.v.$error);
    },

    styles() {
      const { height } = this;
      return { maxHeight: height === null ? 'auto' : `${height}px` };
    },

    containerClasses() {
      const { opened } = this;
      return {
        'form-select__options_hidden': !opened,
        'form-select__options_opened': opened,
      };
    },

    filtered() {
      const {
        filtering,
        filter,
        filter: { length },
        options,
        data,
        dataIds,
        multiple,
      } = this;
      if (!filtering || (length === 0 && data.length === 0)) return options;
      const filteredOptions = options.filter(({ value, text }) => {
        const filterAllow =
          value && text.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
        return multiple
          ? filterAllow && dataIds.indexOf(value) === -1
          : filterAllow;
      });
      return data.concat(filteredOptions);
    },

    filtering() {
      const {
        filterable,
        options: { length },
        size,
      } = this;
      return typeof filterable === 'boolean'
        ? filterable
        : filterable === 'auto' && length > size;
    },

    formClasses() {
      const { hasError, vError, large, wide, quarter } = this;
      return {
        'form-select_wide': wide,
        'form-select_large': large,
        'form-select_has-error': vError || hasError,
        'form-select_quarter': quarter,
        disabled: this.disabled,
      };
    },

    hasError() {
      return this.error && !this.value;
    },

    scrollClasses() {
      const {
        filtered: { length },
        size,
      } = this;
      return {
        'form-select__options_scroll': length > size,
      };
    },

    selected() {
      if (!this.options) {
        return;
      }

      if (!this.multiple) {
        let selected = this.options.filter(
          (item) => item.value !== undefined && item.value == this.value
        );
        return selected.length > 0 ? String(selected[0].text) : undefined;
      } else {
        return this.data.map((item) => item.text).join(', ');
      }
    },

    dataIds() {
      return this.data.map((item) => item.value);
    },
  },

  watch: {
    value: {
      handler: 'watchHandler',
      immediate: true,
    },
    opened(value) {
      if (!value) {
        this.filter = '';
        return;
      }

      this.$nextTick(() => {
        const { input } = this.$refs;
        if (input) {
          input.focus();
        }
      });
    },
    filter(value) {
      this.$emit('filter', value);
    },
  },

  mounted() {
    if (document) {
      document.addEventListener('click', this.close);
    }
  },

  beforeDestroy() {
    if (document) {
      document.removeEventListener('click', this.close);
    }
  },

  methods: {
    watchHandler(value) {
      if (Array.isArray(value)) {
        this.data = value;
      }
    },
    close() {
      this.opened = false;
    },

    input(option) {
      if (!this.multiple) {
        this.opened = false;
        this.$emit('input', option.value);
      } else {
        const index = this.dataIds.indexOf(option.value);
        if (index === -1) {
          this.data.push(option);
        } else {
          this.data.splice(index, 1);
        }
        this.$emit('input', this.dataIds);
      }
    },

    open() {
      this.opened = true;
    },

    setHeight() {
      const { options } = this.$refs;
      if (!options) {
        this.height = null;
        return;
      }

      const $el = options;
      const nodes = $el.childNodes;
      const diff = $el.offsetHeight - $el.clientHeight || 0;
      const firstNodes = Array.from(nodes).slice(
        0,
        Math.min(nodes.length, this.size)
      );
      this.height = firstNodes.reduce(
        (sum, { clientHeight }) => sum + clientHeight,
        diff
      );
    },

    toggle() {
      if (this.disabled) return;
      let current = this.opened;
      // Иммитируем клик, чтобы закрыть другие селекты
      if (document) {
        document.getElementsByTagName('body')[0].click();
      }
      this.opened = !current;
      this.$nextTick(() => this.setHeight());
    },

    isSelected(value) {
      return this.dataIds.indexOf(value) !== -1;
    },
  },
};
</script>

<style lang="scss" scoped>
.red {
  color: red;
}
.disabled {
  .form-select__button {
    filter: grayscale(1);
  }
  .__element-container {
    background-color: lightgrey;
    box-shadow: none;
    &:hover {
      box-shadow: none;
    }
  }
}
.form-select {
  position: relative;
  display: inline-block;

  &__label {
    color: #252525;
    display: block;
    font-size: 10px;
    line-height: 1;
    text-transform: uppercase;
    font-family: 'Open Sans', sans-serif;
    margin-bottom: 8px;
    text-align: left;
  }

  &__add {
    width: 100%;
    height: 28px;
    background-color: #23bb67;
    font-size: 10px;
    font-weight: 700;
    line-height: 28px;
    text-align: center;
    text-transform: uppercase;
    color: #fff;
  }

  &_has-error {
    .form-select__value {
      border-color: red;
    }

    .form-select__text {
      color: red;
      font-size: 0.75em;
      position: absolute;
      top: 58px;
    }

    .form-select__placeholder {
      color: red;
    }
  }

  .form-select__button {
    margin-top: 0px;
    height: 100%;
    width: 1.5em;
    right: 0;
    position: absolute;
    background: url('./img/down.svg') center center no-repeat;
    transition: transform 0.2s ease;

    &_opened {
      transform: rotate(0.5turn);
    }

    &_cursor {
      cursor: default;
    }
  }

  &_wide {
    width: 100%;
  }

  &_quarter {
    width: calc(100% - 15px);
    @media only screen and (max-width: 40em) {
      width: 100%;
    }
  }

  &_large {
    .form-select__option,
    .form-select__selected,
    .form-select__placeholder {
      font-weight: normal;
    }

    .form-select__selected,
    .form-select__placeholder {
      padding: 5px 10px;
    }

    .form-select__placeholder {
      font-size: 11px;
    }

    .form-select__selected {
      font-size: 14px;
      text-transform: none;
    }
  }

  &__option,
  &__selected,
  &__placeholder {
    font-weight: 700;
    font-size: 10px;
    text-transform: uppercase;
    padding-left: 10px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__selected,
  &__placeholder {
    padding-right: 26px;
    white-space: nowrap;
  }

  &__option,
  &__selected {
    color: #444;
  }

  &__option {
    line-height: 1.2em;
    padding-right: 10px;
    padding-top: 6px;
    padding-bottom: 6px;

    &:hover {
      background-color: #5897fb;
      color: #fff;
    }

    &_selected {
      background-color: #2c7dfd;
      color: #fff;
    }

    &_selected:hover {
      background-color: #336bc5;
    }
  }

  &__placeholder {
    color: #999;
  }

  & &__options {
    min-width: 100%;
    display: none;
    position: absolute;
    z-index: 1000;
    margin-top: 1px;
    overflow-x: hidden;
    overflow-y: hidden;

    &_scroll {
      overflow-y: scroll;
    }

    &_opened {
      display: block;
    }
  }

  &__no-options {
    font-style: italic;
    font-size: 10px;
    text-align: center;
    color: #222;
    padding-bottom: 4px;
  }

  &__filter {
    &-container {
      box-sizing: border-box;
      padding: 3px;
      font-size: 10px;
      line-height: 1em;
      width: 100%;
      z-index: 3;
    }

    &-input {
      box-sizing: border-box;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.1);
      width: 100%;
      border: 1px solid #ddd;
      border-radius: 2px;
      line-height: 125%;
      font-weight: 700;
      font-size: 10px;
      padding: 6px 6px;
      color: #666;
      text-transform: uppercase;
      transition: box-shadow 0.2s ease;

      &:focus {
        outline: none;
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// Select.vue?53d2d232